/**
 * Zakładka „Wnioski i zmiany" w profilu (spec 01 §3 zakładka 8).
 *
 * Karta wniosku pokazuje typ, datę i status, a „Porównaj zmiany" rozwija panel
 * z dwiema kolumnami: obecną i proponowaną wartością. Decyzję („Zatwierdź
 * zmianę" / „Poproś o korektę") widzi wyłącznie rola z uprawnieniem — to ona
 * zapisuje wartość w aktach.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Document,
  EmptyState,
  FormField,
  InfoBanner,
  Modal,
  Spinner,
  StatusPill,
  cx,
  useToast,
} from '@k1p4/ui'
import { api, openFile, type ChangeRequestItem, type RequestStatus } from '../../api/client'
import styles from './ChangeRequestsTab.module.css'
import { useFormat } from '../../format'

const STATUS_TONE: Record<RequestStatus, 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

export function ChangeRequestsTab({
  employeeId,
  canDecide,
}: {
  employeeId: string
  canDecide: boolean
}) {
  const fmt = useFormat()
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const commentId = useId()

  const [expanded, setExpanded] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  const requests = useQuery({
    queryKey: ['change-requests', employeeId],
    queryFn: () => api.changeRequests(`employeeId=${employeeId}`),
  })

  const decide = useMutation({
    mutationFn: (input: { id: string; approve: boolean }) =>
      input.approve
        ? api.approveChangeRequest(input.id)
        : api.rejectChangeRequest(input.id, comment.trim() || undefined),
    onSuccess: (_result, input) => {
      toast.show(t(input.approve ? 'changeRequests.approved' : 'changeRequests.rejectedToast'), {
        tone: input.approve ? 'success' : 'info',
      })
      setRejecting(null)
      setComment('')
      void queryClient.invalidateQueries({ queryKey: ['change-requests', employeeId] })
      /* Zatwierdzenie zmienia akta — profil i audyt muszą się odświeżyć. */
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] })
    },
    onError: () => setError('requestsView.errorGeneric'),
  })

  if (requests.isPending) return <Spinner label={t('common.loading')} />

  const items = requests.data?.items ?? []
  if (items.length === 0) {
    return (
      <EmptyState title={t('changeRequests.empty')} description={t('changeRequests.emptyBody')} />
    )
  }

  return (
    <>
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <div className={styles.list}>
        {items.map((item) => (
          <Card key={item.id} pad="md" className={styles.card}>
            <div className={styles.head}>
              <div>
                <p className={styles.type}>{t(`requestFields.${item.fieldKey}`)}</p>
                <p className={styles.meta}>
                  {t('changeRequests.submitted', {
                    date: fmt.date(item.createdAt, 'long'),
                  })}{' '}
                  ·{' '}
                  {t('changeRequests.approver', {
                    who: item.approverEmail ?? t('changeRequests.noApprover'),
                  })}
                </p>
              </div>
              <StatusPill tone={STATUS_TONE[item.status]}>
                {t(`requestStatus.${item.status}`)}
              </StatusPill>
            </div>

            <Button
              size="sm"
              variant="ghost"
              className={styles.toggle}
              onClick={() => setExpanded(expanded === item.id ? null : item.id)}
            >
              {t(expanded === item.id ? 'changeRequests.hide' : 'changeRequests.compare')}
            </Button>

            {expanded === item.id ? (
              <ComparePanel
                item={item}
                canDecide={canDecide}
                decide={decide}
                onReject={(rejectId) => {
                  setError(null)
                  setRejecting(rejectId)
                }}
              />
            ) : null}
          </Card>
        ))}
      </div>

      <Modal
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title={t('changeRequests.rejectTitle')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setRejecting(null)}>
              {t('changeRequests.cancel')}
            </Button>
            <Button
              disabled={comment.trim().length === 0 || decide.isPending}
              onClick={() => decide.mutate({ id: rejecting!, approve: false })}
            >
              {t('changeRequests.rejectSubmit')}
            </Button>
          </div>
        }
      >
        {/* Overlay modala zakrywa baner zakładki — błąd musi być widoczny TUTAJ. */}
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        <FormField label={t('changeRequests.rejectLabel')} htmlFor={commentId}>
          <textarea
            id={commentId}
            className={styles.textarea}
            rows={4}
            placeholder={t('changeRequests.rejectPlaceholder')}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </FormField>
      </Modal>
    </>
  )
}

function ComparePanel({
  item,
  canDecide,
  decide,
  onReject,
}: {
  item: ChangeRequestItem
  canDecide: boolean
  decide: { isPending: boolean; mutate: (input: { id: string; approve: boolean }) => void }
  onReject: (id: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className={styles.panel}>
      <p className={styles.panelTitle}>{t(`requestFields.${item.fieldKey}`)}</p>

      {item.valuesHidden ? (
        <p className={styles.comment}>{t('changeRequests.valuesHidden')}</p>
      ) : (
      <div className={styles.compare}>
        <div className={styles.before}>
          <p className={styles.columnLabel}>{t('changeRequests.currentValue')}</p>
          <p className={cx(styles.value, !item.beforeValue && styles.valueEmpty)}>
            {item.beforeValue ?? t('changeRequests.empty_value')}
          </p>
        </div>
        <div className={styles.after}>
          <p className={styles.columnLabel}>{t('changeRequests.proposedValue')}</p>
          <p className={styles.valueStrong}>{item.afterValue}</p>
        </div>
      </div>
      )}

      {item.attachmentFileId && item.attachmentName ? (
        <p className={styles.attachment}>
          <Document size={15} />
          {/*
            Pobranie idzie przez `openFile`, jak każdy plik z API — obraz
            demonstracyjny odmawia go komunikatem zamiast nawigacji (ADR-0059).
          */}
          <a
            href={api.attachmentUrl(item.attachmentFileId)}
            download
            onClick={(event) => {
              event.preventDefault()
              openFile(api.attachmentUrl(item.attachmentFileId!))
            }}
          >
            {t('changeRequests.attachment', { name: item.attachmentName })}
          </a>
        </p>
      ) : null}

      {item.comment ? (
        <p className={styles.comment}>{t('changeRequests.comment', { text: item.comment })}</p>
      ) : null}
      {item.decisionComment ? (
        <p className={styles.comment}>
          {t('changeRequests.decisionComment', { text: item.decisionComment })}
        </p>
      ) : null}

      {canDecide && item.status === 'pending' ? (
        <div className={styles.decisions}>
          <Button
            size="sm"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ id: item.id, approve: true })}
          >
            {t('changeRequests.approve')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={decide.isPending}
            onClick={() => onReject(item.id)}
          >
            {t('changeRequests.reject')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
